import React, { useEffect } from "react";

import { css } from "@emotion/css";
import Skeleton from "@mui/material/Skeleton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useBGMContent } from "@/hooks/useBGMContent.ts";
import { useScroll } from "@/hooks/useScroll.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Patient } from "@/models/PatientModel.ts";
import { PractitionerUser } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenMax } from "@/theme/responsive.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";
import { ToggleButton } from "@/uiKit/atoms/ToggleButton.tsx";
import { BGMContainerBody } from "@/uiKit/organisms/BGMLogBook/BGMContainerBody.tsx";

export type BGMTableCardProps = {
  practitioner: PractitionerUser;
  patient: Patient;
  date: DateTime;
};

const MemoizedBGMBody = React.memo(BGMContainerBody);

export const BGMTableCard: React.FC<BGMTableCardProps> = ({ practitioner, patient, date }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const { data, loadNextData, loadPreviousData, kind, setKind, status, request } = useBGMContent(
    practitioner,
    patient,
    date,
  );
  const scroll = useScroll();

  useEffect(() => {
    if (scroll.isNearBottom && kind === "downIsPast") {
      loadNextData();
    }
  });

  return (
    <Card>
      <CardHeader
        title={t("bgmLogBook.cardTitle")}
        action={
          <ToggleButtonGroup color="primary" exclusive className={styles.toggleGroup}>
            <ToggleButton
              id="logbook-down-is-past-button"
              data-action="monitoring.logbook.select-down-is-past"
              value="downIsPast"
              aria-label="downIsPast"
              title={t("bgmLogBook.downIsPast")}
              toggled={kind === "downIsPast"}
              onToggle={() => setKind("downIsPast")}
            >
              <Icons.arrowDown className={styles.icon} />
            </ToggleButton>
            <ToggleButton
              id="logbook-down-is-future-button"
              data-action="monitoring.logbook.select-down-is-future"
              value="downIsFuture"
              aria-label="downIsFuture"
              title={t("bgmLogBook.downIsFuture")}
              toggled={kind === "downIsFuture"}
              onToggle={() => setKind("downIsFuture")}
            >
              <Icons.arrowUp className={styles.icon} />
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />
      <CardContent className={styles.content}>
        {status !== "success" ? (
          <>
            <Skeleton height={53} variant="text" />
            <Skeleton height={600} variant="rounded" />
          </>
        ) : (
          <MemoizedBGMBody
            patientId={patient.id}
            data={data}
            loadTop={kind === "downIsFuture" ? loadNextData : request.hasPreviousPage ? loadPreviousData : undefined}
            loadBottom={kind === "downIsFuture" && request.hasPreviousPage ? loadPreviousData : undefined}
            loadTopText={kind === "downIsFuture" ? t("bgmLogBook.loadOneMoreWeek") : t("bgmLogBook.loadMore")}
            loadBottomText={kind === "downIsFuture" ? t("bgmLogBook.loadOneMoreWeek") : t("bgmLogBook.loadMore")}
          />
        )}
      </CardContent>
      <CardFooter className={styles.footer}>
        {kind === "downIsPast" && <Loader size="L" className={styles.loader} />}
      </CardFooter>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  content: css({
    [ScreenMax.SM]: {
      padding: 0,
    },
  }),
  footer: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: ${theme.spacing(2)};
  `,
  loader: css`
    align-self: center;
    margin: ${theme.spacing(4, 0)};
  `,
  toggleGroup: css`
    .MuiToggleButton-root {
      padding: ${theme.spacing(2)};
    }
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
